import React, { useState } from 'react';
import { useWallet } from '../hooks/useWallet';
import './Bridge.css';

const IBC_CHANNELS = [
  {
    name: 'Osmosis',
    chainId: 'osmosis-1',
    channel: 'channel-110089',
    amitisChannel: 'channel-1',
    denom: 'uosmo',
    symbol: 'OSMO',
    rpc: 'https://osmosis.rpc.kjnodes.com',
    logo: '🌊',
  }
];

const DENOM = 'uamts';
const DECIMALS = 1_000_000;

export default function Bridge() {
  const { wallet, client, fetchBalance } = useWallet();
  const [selectedChain, setSelectedChain] = useState(IBC_CHANNELS[0]);
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState('to_amitis');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleBridge = async () => {
    if (!wallet || !client || !amount) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const amountInt = Math.floor(parseFloat(amount) * DECIMALS).toString();
      const timeoutTimestamp = (Date.now() + 10 * 60 * 1000) * 1_000_000; // 10 min

      if (direction === 'from_amitis') {
        // AMTS → Osmosis via IBC
        const msg = {
          typeUrl: '/ibc.applications.transfer.v1.MsgTransfer',
          value: {
            sourcePort: 'transfer',
            sourceChannel: selectedChain.amitisChannel,
            token: { denom: DENOM, amount: amountInt },
            sender: wallet.address,
            receiver: wallet.address, // Same address on destination
            timeoutHeight: { revisionNumber: '1', revisionHeight: '0' },
            timeoutTimestamp: timeoutTimestamp.toString(),
          }
        };

        const fee = { amount: [{ denom: DENOM, amount: '300000' }], gas: '500000' };
        const tx = await client.signAndBroadcast(wallet.address, [msg], fee);

        if (tx.code === 0) {
          setResult({ txhash: tx.transactionHash });
          setAmount('');
          await fetchBalance(wallet.address);
        } else {
          setError(`Transfer failed: ${tx.rawLog}`);
        }
      } else {
        // Osmosis → Amitis — requires Osmosis wallet/client
        setError('To bridge from Osmosis to Amitis, please use Keplr wallet with Osmosis chain selected and send an IBC transfer to Amitis (channel-110089).');
      }
    } catch (e) {
      setError(e.message || 'Bridge failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-pill mono">IBC Bridge</div>
        <h1 className="page-title">
          Bridge assets<br />
          <span className="italic green">across chains.</span>
        </h1>
        <p className="page-sub">
          Move tokens between Amitis and other Cosmos chains via IBC. Trustless, direct, no custodians.
        </p>
      </div>

      <div className="chains-grid">
        {IBC_CHANNELS.map(chain => (
          <div
            key={chain.chainId}
            className={`chain-card ${selectedChain.chainId === chain.chainId ? 'selected' : ''}`}
            onClick={() => setSelectedChain(chain)}
          >
            <span className="chain-logo">{chain.logo}</span>
            <div className="chain-info">
              <strong>{chain.name}</strong>
              <span className="mono muted">channel-1 ↔ {chain.channel}</span>
            </div>
            <div className="chain-status">
              <span className="status-dot" />
              <span className="mono">Live</span>
            </div>
          </div>
        ))}
      </div>

      <div className="bridge-card">
        <div className="direction-toggle">
          <button
            className={`direction-btn ${direction === 'to_amitis' ? 'active' : ''}`}
            onClick={() => { setDirection('to_amitis'); setAmount(''); setError(null); setResult(null); }}
          >
            {selectedChain.name} → Amitis
          </button>
          <button
            className={`direction-btn ${direction === 'from_amitis' ? 'active' : ''}`}
            onClick={() => { setDirection('from_amitis'); setAmount(''); setError(null); setResult(null); }}
          >
            Amitis → {selectedChain.name}
          </button>
        </div>

        <div className="bridge-section">
          <label className="mono muted">From</label>
          <div className="bridge-chain-label">
            <span>{direction === 'to_amitis' ? selectedChain.logo : '⬡'}</span>
            <strong>{direction === 'to_amitis' ? selectedChain.name : 'Amitis Network'}</strong>
          </div>
          <div className="token-input" style={{marginTop: '12px'}}>
            <div className="token-input-header">
              <span className="mono muted">Amount</span>
            </div>
            <div className="token-input-body">
              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={e => { setAmount(e.target.value); setError(null); setResult(null); }}
                className="amount-input"
              />
              <button className="token-select">
                {direction === 'to_amitis' ? selectedChain.symbol : 'AMTS'}
                <span>▼</span>
              </button>
            </div>
          </div>
        </div>

        <div className="bridge-arrow">
          <div className="bridge-arrow-line" />
          <span>IBC Transfer</span>
          <div className="bridge-arrow-line" />
        </div>

        <div className="bridge-section">
          <label className="mono muted">To</label>
          <div className="bridge-chain-label">
            <span>{direction === 'to_amitis' ? '⬡' : selectedChain.logo}</span>
            <strong>{direction === 'to_amitis' ? 'Amitis Network' : selectedChain.name}</strong>
          </div>
          {wallet && (
            <div className="bridge-address mono">{wallet.address}</div>
          )}
        </div>

        <div className="ibc-info">
          <div className="ibc-info-row">
            <span className="mono muted">Channel</span>
            <span className="mono">channel-1 ↔ {selectedChain.channel}</span>
          </div>
          <div className="ibc-info-row">
            <span className="mono muted">Estimated time</span>
            <span className="mono">~30 seconds</span>
          </div>
          <div className="ibc-info-row">
            <span className="mono muted">Fee</span>
            <span className="mono green">0.06% effective (80% rebate)</span>
          </div>
        </div>

        <button
          className="bridge-btn"
          disabled={!wallet || !amount || loading}
          onClick={handleBridge}
        >
          {!wallet ? 'Connect Wallet to Bridge' : loading ? 'Bridging...' : `Bridge ${direction === 'to_amitis' ? selectedChain.symbol : 'AMTS'}`}
        </button>

        {!wallet && (
          <p className="bridge-note mono muted">
            IBC bridge requires a connected wallet to sign the transfer transaction.
          </p>
        )}

        {error && <div className="claim-error"><span>⚠️</span> {error}</div>}
        {result && (
          <div className="claim-success">
            <div className="success-icon">✓</div>
            <div className="success-content">
              <strong>Transfer initiated!</strong>
              <span className="mono">tx: {result.txhash?.slice(0, 20)}...</span>
              <p className="success-note">IBC transfers typically complete within 30 seconds.</p>
            </div>
          </div>
        )}
      </div>

      <div className="ibc-explainer">
        <h3>How IBC works</h3>
        <div className="ibc-steps">
          <div className="ibc-step">
            <div className="step-num">1</div>
            <div className="step-content">
              <strong>Lock on source chain</strong>
              <span>Your tokens are locked in an escrow on the sending chain</span>
            </div>
          </div>
          <div className="ibc-step">
            <div className="step-num">2</div>
            <div className="step-content">
              <strong>Relayer delivers proof</strong>
              <span>Hermes relayer submits cryptographic proof to the destination chain</span>
            </div>
          </div>
          <div className="ibc-step">
            <div className="step-num">3</div>
            <div className="step-content">
              <strong>Receive on destination</strong>
              <span>Equivalent tokens are minted or unlocked on the destination chain</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
